import {
  LegacyPositionRow,
  legacyPositionRows,
} from 'structure/capital-manager/constants/VaultsLegacy';
import {
  LegacyPositionBalancesMap,
  useLegacyPositionsBalances,
} from 'structure/capital-manager/hooks/useLegacyPositionsBalances';
import { Body2, Header3 } from 'components/Typography';
import { formatTokenBase } from '@dapp/multi-lib/lib/formatters';
import { base, bsc, mainnet } from 'wagmi/chains';
import { Abi, formatUnits } from 'viem';
import { useConnection, useSwitchChain, useWriteContract } from 'wagmi';
import React, { useMemo, useState } from 'react';
import { waitForTransaction } from 'utils/waitForTransaction';
import { getTxErrorMessage } from 'utils/getTxErrorMessage';
import legacyBondedAbi from '@singularitydao/v1-farming-contracts/abi/SDAOBondedTokenStake.json';
import legacyStakingAbi from 'utils/constants/abi/SDAOTokenStaking';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { TokenImage } from 'components/TokenImage';
import { getNetworkByChainId } from '@dapp/multi-lib/network';
import { getNetworkIconUrlByChaiId } from 'utils/url';

type LegacyPositionAction = 'claim' | 'withdraw';

const getWagmiChainById = (chainId: number) => {
  if (chainId === mainnet.id) return mainnet;
  if (chainId === bsc.id) return bsc;
  if (chainId === base.id) return base;
  return null;
};

export const LegacyPositionsSection = () => {
  const connection = useConnection();
  const toastV2 = useToast();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();

  const legacyBalancesQuery = useLegacyPositionsBalances({
    legacyPositions: legacyPositionRows,
    userAddress: connection.address,
  });

  const [activeLegacyAction, setActiveLegacyAction] = useState<string | null>(
    null
  );

  const handleLegacyPositionAction = async (
    position: LegacyPositionRow,
    action: LegacyPositionAction
  ) => {
    if (!connection.address) {
      toastV2({
        title: 'Wallet not connected',
        description: 'Connect your wallet to interact with legacy positions.',
        type: 'error',
      });
      return;
    }

    const userBalance = legacyBalancesQuery.data?.[position.id];
    const balance = userBalance?.balance || BigInt(0);
    if (action === 'withdraw' && balance <= BigInt(0)) {
      return;
    }

    const actionKey = `${position.id}-${action}`;
    setActiveLegacyAction(actionKey);

    try {
      if (connection.chainId !== position.chainId) {
        await switchChainAsync?.({ chainId: position.chainId });
      }

      const txChain = getWagmiChainById(position.chainId);
      if (!txChain) {
        throw new Error(`Unsupported chain: ${position.chainId}`);
      }

      let hash: `0x${string}`;
      if (position.category === 'BONDED') {
        if (action === 'claim') {
          hash = await writeContractAsync({
            address: position.poolAddress as `0x${string}`,
            abi: legacyBondedAbi as Abi,
            functionName: 'claimStake',
            args: [],
            chain: txChain,
            account: connection.address,
            chainId: position.chainId,
          });
        } else {
          hash = await writeContractAsync({
            address: position.poolAddress as `0x${string}`,
            abi: legacyBondedAbi as Abi,
            functionName: 'withdrawStake',
            args: [balance],
            chain: txChain,
            account: connection.address,
            chainId: position.chainId,
          });
        }
      } else if (action === 'claim') {
        hash = await writeContractAsync({
          address: position.poolAddress as `0x${string}`,
          abi: legacyStakingAbi,
          functionName: 'harvest',
          args: [BigInt(position.poolId), connection.address],
          chain: txChain,
          account: connection.address,
          chainId: position.chainId,
        });
      } else {
        hash = await writeContractAsync({
          address: position.poolAddress as `0x${string}`,
          abi: legacyStakingAbi,
          functionName: 'withdrawAndHarvest',
          args: [BigInt(position.poolId), balance, connection.address],
          chain: txChain,
          account: connection.address,
          chainId: position.chainId,
        });
      }

      await waitForTransaction({
        chainId: position.chainId,
        hash,
        connectorId: connection.connector?.id,
      });

      toastV2({
        title: 'Transaction successful',
        description: `${action === 'claim' ? 'Claim' : 'Withdraw'} completed.`,
        type: 'success',
      });

      await legacyBalancesQuery.refetch();
    } catch (error) {
      const message = getTxErrorMessage(error as Error, 'Transaction failed');
      toastV2({
        title: 'Transaction failed',
        description: message,
        type: 'error',
      });
    } finally {
      setActiveLegacyAction(null);
    }
  };

  const legacyPositionRowsFiltered = useMemo(() => {
    return legacyPositionRows.filter((position) => {
      const isUserHasBalance =
        (legacyBalancesQuery.data?.[position.id]?.balance || BigInt(0)) >
        BigInt(0);
      const isUserHasPending =
        (legacyBalancesQuery.data?.[position.id]?.pending || BigInt(0)) >
        BigInt(0);
      return isUserHasBalance || isUserHasPending;
    });
  }, [legacyBalancesQuery.data]);

  if (legacyPositionRowsFiltered.length === 0) {
    return null;
  }

  return (
    <LegacyPositionsContent
      positions={legacyPositionRowsFiltered}
      balancesMap={legacyBalancesQuery.data}
      isBalancesLoading={legacyBalancesQuery.isLoading}
      isWalletConnected={!!connection.address}
      activeAction={activeLegacyAction}
      onAction={handleLegacyPositionAction}
    />
  );
};

interface LegacyPositionsContentProps {
  positions: LegacyPositionRow[];
  balancesMap?: LegacyPositionBalancesMap;
  isBalancesLoading: boolean;
  isWalletConnected: boolean;
  activeAction: string | null;
  onAction: (
    position: LegacyPositionRow,
    action: LegacyPositionAction
  ) => Promise<void>;
}

const LegacyPositionsContent = (props: LegacyPositionsContentProps) => {
  const {
    positions,
    balancesMap,
    isBalancesLoading,
    isWalletConnected,
    activeAction,
    onAction,
  } = props;

  const getUserBalanceLabel = (position: LegacyPositionRow) => {
    if (!isWalletConnected) {
      return 'Connect wallet';
    }
    if (isBalancesLoading) {
      return 'Loading...';
    }

    const userBalance = balancesMap?.[position.id];
    const amount = formatLegacyUserAmount(
      userBalance?.balance,
      userBalance?.tokenDecimals
    );
    const symbol = userBalance?.tokenSymbol || '';
    return `${amount}${symbol ? ` ${symbol}` : ''}`;
  };

  const getUserPendingLabel = (position: LegacyPositionRow) => {
    if (!isWalletConnected) {
      return 'Connect wallet';
    }
    if (isBalancesLoading) {
      return 'Loading...';
    }

    const userBalance = balancesMap?.[position.id];
    const amount = formatLegacyUserAmount(
      userBalance?.pending,
      userBalance?.tokenDecimals
    );
    const symbol = userBalance?.tokenSymbol || '';
    return `${amount}${symbol ? ` ${symbol}` : ''}`;
  };

  const getIsActionDisabled = (
    position: LegacyPositionRow,
    action: LegacyPositionAction
  ) => {
    if (!isWalletConnected || isBalancesLoading || !!activeAction) {
      return true;
    }

    const userBalance = balancesMap?.[position.id];

    if (action === 'withdraw') {
      return (userBalance?.balance || BigInt(0)) <= BigInt(0);
    }

    if (position.category !== 'BONDED') {
      return (userBalance?.pending || BigInt(0)) <= BigInt(0);
    }

    return true;
  };

  const getActionLabel = (
    position: LegacyPositionRow,
    action: LegacyPositionAction
  ) => {
    if (activeAction === `${position.id}-${action}`) {
      return action === 'claim' ? 'Claiming...' : 'Withdrawing...';
    }

    return action === 'claim' ? 'Claim' : 'Withdraw';
  };

  return (
    <div className='mt-10 flex flex-col gap-4'>
      <Header3>Deprecated legacy positions</Header3>
      <Body2 className='text-[#B5B5B5]'>
        Legacy positions with wallet-specific balances.
      </Body2>
      <div className='overflow-x-auto'>
        <table className='min-w-[1250px] border-spacing-2'>
          <thead>
            <tr>
              <th className='px-4 py-2 text-left'>
                <Body2 className='text-[#B5B5B5]'>Chain</Body2>
              </th>
              <th className='px-4 py-2 text-left'>
                <Body2 className='text-[#B5B5B5]'>Name</Body2>
              </th>
              <th className='px-4 py-2 text-left'>
                <Body2 className='text-[#B5B5B5]'>Category</Body2>
              </th>
              <th className='px-4 py-2 text-left'>
                <Body2 className='text-[#B5B5B5]'>Status</Body2>
              </th>
              <th className='px-4 py-2 text-left'>
                <Body2 className='text-[#B5B5B5]'>Tokens</Body2>
              </th>
              <th className='px-4 py-2 text-left'>
                <Body2 className='text-[#B5B5B5]'>My balance</Body2>
              </th>
              <th className='px-4 py-2 text-left'>
                <Body2 className='text-[#B5B5B5]'>My pending</Body2>
              </th>
              <th className='px-4 py-2 text-left'>
                <Body2 className='text-[#B5B5B5]'>Actions</Body2>
              </th>
            </tr>
          </thead>
          <tbody>
            {positions.map((position) => (
              <tr key={position.id} className='py-3'>
                <td className='border-b border-b-gray-800 px-4 py-4'>
                  <div className='flex flex-row items-center gap-2'>
                    <div className='flex min-h-[32px] min-w-[32px]'>
                      <TokenImage
                        symbol={
                          getNetworkByChainId(position.chainId)?.shortName ?? ''
                        }
                        src={getNetworkIconUrlByChaiId(position.chainId)}
                        alt={
                          getNetworkByChainId(position.chainId)?.shortName ?? ''
                        }
                        width={32}
                        height={32}
                      />
                    </div>
                    <Body2>{getChainLabel(position.chainId)}</Body2>
                  </div>
                </td>
                <td className='border-b border-b-gray-800 px-4 py-4'>
                  <Body2 className='font-bold'>{position.name}</Body2>
                </td>
                <td className='border-b border-b-gray-800 px-4 py-4'>
                  <Body2>{position.category}</Body2>
                </td>

                <td className='border-b border-b-gray-800 px-4 py-4'>
                  <Body2>{position.status}</Body2>
                </td>
                <td className='border-b border-b-gray-800 px-4 py-4'>
                  <Body2>{position.tokens.join(' / ')}</Body2>
                </td>
                <td className='border-b border-b-gray-800 px-4 py-4'>
                  <Body2>{getUserBalanceLabel(position)}</Body2>
                </td>
                <td className='border-b border-b-gray-800 px-4 py-4'>
                  <Body2>{getUserPendingLabel(position)}</Body2>
                </td>
                <td className='border-b border-b-gray-800 px-4 py-4'>
                  <div className='flex gap-2'>
                    <button
                      type='button'
                      className='rounded-md border border-gray-700 px-3 py-1 text-xs text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50'
                      disabled={getIsActionDisabled(position, 'claim')}
                      onClick={() => onAction(position, 'claim')}
                    >
                      {getActionLabel(position, 'claim')}
                    </button>
                    <button
                      type='button'
                      className='rounded-md border border-gray-700 px-3 py-1 text-xs text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50'
                      disabled={getIsActionDisabled(position, 'withdraw')}
                      onClick={() => onAction(position, 'withdraw')}
                    >
                      {getActionLabel(position, 'withdraw')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const getChainLabel = (chainId: number) => {
  if (chainId === 1) return 'Ethereum';
  if (chainId === 56) return 'BSC';
  if (chainId === 8453) return 'Base';
  return `Chain ${chainId}`;
};

const formatLegacyUserAmount = (amount?: bigint, decimals?: number) => {
  if (amount === undefined) {
    return '0';
  }

  return formatTokenBase(formatUnits(amount, decimals ?? 18));
};
